import React from 'react';
import { Box, Typography, Divider, Grid } from '@mui/material';

// A4-style printable prescription (Rx) sheet.
// Props: clinic (Settings doc), doctor (User), patient (Patient), visit (Visit), consultation (Consultation)
const PrintablePrescription = ({ clinic, doctor, patient, visit, consultation }) => {
  if (!consultation) return null;

  const today = new Date(consultation.createdAt || Date.now());

  return (
    <Box
      className="rx-print-sheet"
      sx={{
        width: '210mm',
        minHeight: '148mm',
        bgcolor: '#fff',
        color: '#2b2740',
        p: '14mm',
        mx: 'auto',
        fontFamily: '"Inter", sans-serif',
      }}
    >
      {/* Letterhead */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #8B7FD6', pb: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 800, fontSize: '1.5rem', color: '#6B5FC4' }}>
            {clinic?.clinicName || 'EyeDealist Eye Clinic'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#7A748F' }}>
            {clinic?.tagline || 'Complete Eye Care'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#948EA6', mt: 0.5 }}>
            {[clinic?.address?.line1, clinic?.address?.city, clinic?.address?.state, clinic?.address?.pincode].filter(Boolean).join(', ')}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#948EA6' }}>
            {clinic?.phone && `Phone: ${clinic.phone}`} {clinic?.email && ` · ${clinic.email}`}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="subtitle1" fontWeight={700}>Dr. {doctor?.name}</Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#7A748F' }}>
            {doctor?.qualification || 'MBBS, MS (Ophthalmology)'}
          </Typography>
          {doctor?.registrationNumber && (
            <Typography variant="caption" sx={{ display: 'block', color: '#7A748F' }}>
              Reg. No: {doctor.registrationNumber}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Patient details bar */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={4}><Typography variant="body2"><strong>Patient:</strong> {patient?.name}</Typography></Grid>
        <Grid item xs={2}><Typography variant="body2"><strong>Age/Sex:</strong> {patient?.age}/{patient?.gender?.charAt(0)}</Typography></Grid>
        <Grid item xs={3}><Typography variant="body2"><strong>Patient ID:</strong> {patient?.patientId}</Typography></Grid>
        <Grid item xs={3}><Typography variant="body2"><strong>Date:</strong> {today.toLocaleDateString('en-IN')}</Typography></Grid>
        {visit?.visitId && (
          <Grid item xs={4}><Typography variant="body2"><strong>Visit ID:</strong> {visit.visitId}</Typography></Grid>
        )}
      </Grid>

      <Divider sx={{ mb: 2 }} />

      {/* Clinical summary */}
      {consultation.chiefComplaint && (
        <Typography variant="body2" sx={{ mb: 1 }}><strong>Chief Complaint:</strong> {consultation.chiefComplaint}</Typography>
      )}
      {consultation.diagnosis?.primary && (
        <Typography variant="body2" sx={{ mb: 1 }}><strong>Diagnosis:</strong> {consultation.diagnosis.primary}</Typography>
      )}

      {/* Rx Symbol + medicines */}
      <Box sx={{ mt: 3, mb: 3 }}>
        <Typography sx={{ fontFamily: '"Plus Jakarta Sans", serif', fontSize: '2rem', fontWeight: 700, color: '#8B7FD6', mb: 1 }}>
          ℞
        </Typography>
        {consultation.prescriptions?.length > 0 ? (
          <Box sx={{ pl: 2 }}>
            {consultation.prescriptions.map((p, i) => (
              <Box key={i} sx={{ mb: 1.5 }}>
                <Typography variant="body1" fontWeight={600}>
                  {i + 1}. {p.medicine?.name || p.medicine} {p.medicine?.strength ? `(${p.medicine.strength})` : ''}
                </Typography>
                <Typography variant="body2" sx={{ color: '#7A748F', pl: 2 }}>
                  {p.dosage} — {p.frequency} — {p.duration}
                  {p.instructions ? ` (${p.instructions})` : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>No medicines prescribed</Typography>
        )}
      </Box>

      {consultation.treatmentPlan && (
        <Typography variant="body2" sx={{ mb: 1 }}><strong>Advice:</strong> {consultation.treatmentPlan}</Typography>
      )}

      {consultation.investigations?.ordered?.length > 0 && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Investigations Advised:</strong> {consultation.investigations.ordered.join(', ')}
        </Typography>
      )}

      {consultation.followUp?.required && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Follow-up:</strong> After {consultation.followUp.afterDays} day(s)
          {consultation.followUp.notes ? ` — ${consultation.followUp.notes}` : ''}
        </Typography>
      )}

      {/* Signature */}
      <Box sx={{ mt: 6, display: 'flex', justifyContent: 'flex-end' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ borderTop: '1px solid #948EA6', width: '180px', pt: 1 }}>
            <Typography variant="body2" fontWeight={600}>Dr. {doctor?.name}</Typography>
            <Typography variant="caption" color="text.secondary">Signature</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PrintablePrescription;
